import React from 'react';
import {Box, Grid} from '@mui/material';
import {EntioCard} from 'src/components/ui';
import {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {useAppSelector, useAppDispatch} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
// import {RoomStatusEnum} from 'src/apis/autoGenerate';
import {
  fetchAllPropertyData,
  selectPropertyStats,
  selectLastFetched as selectPropertyLastFetched,
  selectApartments, // Add this import
} from 'src/redux/slice/propertyCacheSlice';
import {
  fetchDeviceStats,
  selectDeviceStats,
  selectDeviceStatsLastFetched,
  selectDevice,
} from 'src/redux/slice/deviceSlice';
import {fetchTenantStats, selectTenantStats} from 'src/redux/slice/residentSlice';
import WeatherCard from 'src/components/DashboardCards/WeatherCard';
import DeviceStatsCard from 'src/components/DashboardCards/DeviceStatsCard';
import OccupancyCard from 'src/components/DashboardCards/OccupancyCard';
import QuickActionsCard from 'src/components/DashboardCards/QuickActionsCard';
import RecentActivitiesCard from 'src/components/DashboardCards/RecentActivitiesCard';
import EventSummaryCard from 'src/components/DashboardCards/EventSummaryCard';
import ResidentStatusCard from 'src/components/DashboardCards/ResidentStatusCard';
import PropertyCard from 'src/components/DashboardCards/PropertyCard';
import AddTenantModal from 'src/components/Modals/AddTenant';
import AddDeliveryModal from 'src/components/Modals/AddDelivery';
import {useCanEdit} from 'src/hooks';
import {usePdkResidentLock} from 'src/hooks/usePdkResidentLock';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Home() {
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccount);
  const canEdit = useCanEdit();
  const pdkResidentsLocked = usePdkResidentLock();

  // Modal states
  const [addTenantModalOpen, setAddTenantModalOpen] = useState(false);
  const [addDeliveryModalOpen, setAddDeliveryModalOpen] = useState(false);

  // Replace this line:
  // const property = useAppSelector(selectProperty);
  // With this:
  const apartments = useAppSelector(selectApartments);

  // Get data from Redux caches
  const propertyStats = useAppSelector(selectPropertyStats);
  const propertyLastFetched = useAppSelector(selectPropertyLastFetched);
  const deviceStats = useAppSelector(selectDeviceStats) || {totalDevices: 0, onlineDevices: 0};
  const deviceLastFetched = useAppSelector(selectDeviceStatsLastFetched);
  const tenantStats = useAppSelector(selectTenantStats);
  const deviceState = useAppSelector(selectDevice);

  useEffect(() => {
    // Get orgId from either role or organization field
    const orgId = account.role?.org_id || account.organization?.id;
    const userId = account.userId;

    if (!orgId) {
      return;
    }

    const thirtyMinutesInMs = 30 * 60 * 1000;

    // Check property data cache
    const needsPropertyRefresh =
      !propertyLastFetched || Date.now() - propertyLastFetched > thirtyMinutesInMs;
    if (needsPropertyRefresh) {
      dispatch(fetchAllPropertyData({orgId, userId}));
    }

    // Check device data cache
    const needsDeviceRefresh =
      !deviceLastFetched || Date.now() - deviceLastFetched > thirtyMinutesInMs;
    if (needsDeviceRefresh) {
      dispatch(fetchDeviceStats({orgId, userId}));
    }

    // Fetch tenant stats with userId for permission filtering
    dispatch(fetchTenantStats({orgId, userId}));
  }, [
    account.role?.org_id,
    account.organization?.id,
    account.userId,
    propertyLastFetched,
    deviceLastFetched,
    dispatch,
  ]);

  // 获取设备ID列表用于事件统计
  const deviceIds = useMemo(() => {
    if (!deviceState?.deviceList || deviceState.deviceList.length === 0) {
      return undefined;
    }

    // 方案1: 获取所有设备（推荐）
    const allDeviceIds = deviceState.deviceList
      .map(device => device.id)
      .filter((id): id is string => id !== undefined)
      .join(',');

    // 方案2: 如果担心性能问题，可以设置一个合理的上限
    // const maxDevices = 20; // 可以根据实际情况调整
    // const limitedDeviceIds = deviceState.deviceList
    //   .slice(0, maxDevices)
    //   .map(device => device.id)
    //   .filter((id): id is string => id !== undefined)
    //   .join(',');

    return allDeviceIds;
  }, [deviceState?.deviceList]);

  // 获取设备ID数组用于最近活动
  const deviceIdsForActivities = useMemo(() => {
    if (!deviceState?.deviceList || deviceState.deviceList.length === 0) {
      return [];
    }
    // 获取前10个设备的ID数组，过滤掉undefined值
    return deviceState.deviceList
      .slice(0, 10)
      .map(device => device.id)
      .filter((id): id is string => id !== undefined);
  }, [deviceState?.deviceList]);

  return (
    <Box sx={{flexGrow: 1, p: {xs: 1, sm: 2}}}>
      {/* 呼叫/通话功能组件 */}
      {/* <CallPanel /> */}

      <Grid container spacing={{xs: 1, sm: 2}}>
        {/* First row */}
        <Grid item xs={12} sm={6} md={4}>
          <EntioCard sx={{minHeight: {xs: '180px', sm: '220px'}}}>
            <WeatherCard apartments={apartments} deviceList={deviceState?.deviceList} />
          </EntioCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DeviceStatsCard
            totalDevices={deviceStats.totalDevices}
            onlineDevices={deviceStats.onlineDevices}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PropertyCard
            totalProperties={propertyStats.totalApartments}
            totalResidents={tenantStats.length}
          />
        </Grid>

        {/* Second row */}
        <Grid item xs={12} sm={6} md={4}>
          <ResidentStatusCard />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <EntioCard title="Occupancy" sx={{minHeight: {xs: '200px', sm: '220px'}}}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <OccupancyCard
                occupied={propertyStats.occupiedRooms}
                vacant={propertyStats.vacantRooms}
              />
            </Box>
          </EntioCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <EventSummaryCard deviceIds={deviceIds} />
        </Grid>

        {/* Third row */}
        <Grid item xs={12} md={6}>
          <RecentActivitiesCard deviceIds={deviceIdsForActivities} />
        </Grid>
        <Grid item xs={12} md={6}>
          <QuickActionsCard
            disableAddResident={pdkResidentsLocked}
            onAddResident={() => {
              if (!pdkResidentsLocked) setAddTenantModalOpen(true);
            }}
            onAddDelivery={() => {
              if (canEdit) {
                setAddDeliveryModalOpen(true);
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Modals */}
      <AddTenantModal
        open={addTenantModalOpen}
        handleCancel={() => setAddTenantModalOpen(false)}
        handleConfirm={() => {
          setAddTenantModalOpen(false);
          // Refresh tenant stats after adding
          const orgId = account.role?.org_id || account.organization?.id;
          const userId = account.userId;
          if (orgId && userId) {
            dispatch(fetchTenantStats({orgId, userId}));
          }
        }}
      />
      <AddDeliveryModal
        open={addDeliveryModalOpen}
        handleCancel={() => setAddDeliveryModalOpen(false)}
        handleConfirm={() => {
          setAddDeliveryModalOpen(false);
        }}
      />
    </Box>
  );
}
